import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Edit2, Trash2, Building2, User, CheckCircle,
    XCircle, Loader2, Settings, Car, Home, Smartphone, Package
} from 'lucide-react';
import { assetService } from '@services/assetService';
import { companyService } from '@services/companyService';
import { useAuth } from '@context/AuthContext';
import CustomSelect from '../shared/CustomSelect';

const AssetRequirementsContent = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [requirements, setRequirements] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCompany, setSelectedCompany] = useState('');

    useEffect(() => {
        fetchCompanies();
        fetchRequirements();
    }, [selectedCompany]);

    const fetchCompanies = async () => {
        try {
            const response = await companyService.getAll();
            if (response.success && response.data) {
                setCompanies(response.data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchRequirements = async () => {
        try {
            setLoading(true);
            let response;
            if (selectedCompany) {
                response = await assetService.assetRequirements.getByCompany(selectedCompany);
            } else {
                response = await assetService.assetRequirements.getAll();
            }

            if (response.success && response.data) {
                setRequirements(response.data);
            }
        } catch (error) {
            console.error('Error fetching requirements:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this asset requirement?')) {
            try {
                await assetService.assetRequirements.delete(id);
                fetchRequirements();
            } catch (error) {
                console.error('Error deleting requirement:', error);
            }
        }
    };

    const getAssetTypeIcon = (type) => {
        switch (type) {
            case 'VEHICLE': return Car;
            case 'LAND':
            case 'PROPERTY': return Home;
            case 'PHONE': return Smartphone;
            default: return Package;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Asset Requirements</h2>
                    <p className="text-gray-600 mt-1">
                        Manage asset types and requirements per company
                    </p>
                </div>
                {hasPermission('ASSET_CONFIG:CREATE') && (
                    <button
                        onClick={() => navigate('/compliance/assets/requirements/new')}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Add Requirement
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-4">
                    <label className="block text-sm font-medium text-gray-700">
                        Filter by Company
                    </label>
                    <CustomSelect
                        value={selectedCompany}
                        onChange={setSelectedCompany}
                        options={[
                            { value: '', label: 'All Companies' },
                            ...companies.map(company => ({
                                value: company.id,
                                label: company.name
                            }))
                        ]}
                        placeholder="All Companies"
                        searchable
                    />
                </div>
            </div>

            {/* Requirements List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                ) : requirements.length === 0 ? (
                    <div className="text-center py-12">
                        <Settings className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No asset requirements found</p>
                        <button
                            onClick={() => navigate('/compliance/assets/requirements/new')}
                            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                        >
                            Add your first requirement
                        </button>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Requirement
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Asset Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Company
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Client Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Photos
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Mandatory
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                        {requirements.map((req) => {
                            const Icon = getAssetTypeIcon(req.assetType);
                            return (
                                <tr key={req.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{req.name}</p>
                                            {req.description && (
                                                <p className="text-xs text-gray-500 truncate max-w-xs">
                                                    {req.description}
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <Icon className="w-4 h-4 text-gray-600" />
                                            <span className="text-sm text-gray-900">{req.assetType}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {req.companyName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-1">
                                            {req.applicableClientType === 'RETAIL' ? (
                                                <User className="w-4 h-4 text-gray-600" />
                                            ) : (
                                                <Building2 className="w-4 h-4 text-gray-600" />
                                            )}
                                            <span className="text-sm text-gray-900">{req.applicableClientType}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {req.minimumPhotos} - {req.maximumPhotos}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {req.mandatory ? (
                                            <span className="flex items-center gap-1 text-green-600">
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span className="text-sm">Yes</span>
                                                </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-gray-400">
                                                    <XCircle className="w-4 h-4" />
                                                    <span className="text-sm">No</span>
                                                </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                req.active
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {req.active ? 'Active' : 'Inactive'}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            {hasPermission('ASSET_CONFIG:UPDATE') && (
                                                <button
                                                    onClick={() => navigate(`/compliance/assets/requirements/${req.id}/edit`)}
                                                    className="text-gray-600 hover:text-indigo-600 transition"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                            )}
                                            {hasPermission('ASSET_CONFIG:DELETE') && (
                                                <button
                                                    onClick={() => handleDelete(req.id)}
                                                    className="text-gray-600 hover:text-red-600 transition"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AssetRequirementsContent;
